import { Controller } from "@hotwired/stimulus"
import { Turbo, cable } from "@hotwired/turbo-rails"
import { Modal } from "bootstrap"

// Invalidate polls across bell and inbox instances when either records a read.
let revision = 0
let readInFlight = false

export default class extends Controller {
  static targets = ["badge", "unreadStatus", "content", "loading", "error"]
  static values = { statusUrl: String, recentUrl: String, inboxUrl: String, intervalSeconds: { type: Number, default: 1800 } }

  connect() {
    if (!this.hasStatusUrlValue && !this.hasInboxUrlValue) return
    this.lifecycle = {}
    if (this.hasStatusUrlValue) {
      this.refresh()
      this.subscribe(this.lifecycle)
    }
    this.interval = setInterval(() => this.refreshAuthoritative(), this.intervalSecondsValue * 1000)
  }

  disconnect() {
    this.lifecycle = null
    this.cableConnected = false
    this.subscription?.unsubscribe()
    this.subscription = null
    clearTimeout(this.signalTimer)
    this.signalTimer = null
    clearInterval(this.interval)
    this.request?.abort()
  }

  async subscribe(lifecycle) {
    try {
      const subscription = await cable.subscribeTo("NotificationsChannel", {
        connected: () => {
          if (this.lifecycle !== lifecycle) return
          this.cableConnected = true
          this.scheduleSignal()
        },
        disconnected: () => {
          if (this.lifecycle === lifecycle) this.cableConnected = false
        },
        received: data => {
          if (this.lifecycle === lifecycle && data?.event === "notifications_changed") this.scheduleSignal()
        }
      })
      if (this.lifecycle === lifecycle) this.subscription = subscription
      else subscription.unsubscribe()
    } catch (_error) {
      // HTTP polling and visibility refresh remain available without Cable.
    }
  }

  scheduleSignal() {
    if (this.signalTimer) return
    this.signalTimer = setTimeout(() => {
      this.signalTimer = null
      if (!this.element.isConnected) return
      revision += 1
      this.dispatch("signal", { target: document })
    }, 50)
  }

  signalled() {
    this.refreshAuthoritative()
  }

  refreshAuthoritative() {
    this.reloadInbox = this.hasInboxUrlValue
    this.refresh()
  }

  resume() {
    if (!this.refreshPending) return
    this.refreshPending = false
    this.refresh()
  }

  visible() {
    if (!document.hidden) this.refreshAuthoritative()
  }

  changed(event) {
    if (event.detail.origin !== this) this.refresh()
  }

  acknowledged(event) {
    if (this.hasInboxUrlValue) {
      const rows = Array.from(this.contentTarget.querySelectorAll("[data-notification-key]")).filter(row =>
        row.dataset.notificationSource === event.detail.sourceKey && row.dataset.notificationKey === event.detail.itemKey)
      this.markRowsRead(rows)
    }
    this.refresh()
  }

  async refresh() {
    if ((!this.hasStatusUrlValue && !this.hasInboxUrlValue) || document.hidden) return
    if (this.request || this.saving || readInFlight) {
      this.refreshPending = true
      return
    }
    const reloadInbox = this.reloadInbox
    this.reloadInbox = false
    let mode = "status"
    let url = this.statusUrlValue
    if (this.hasInboxUrlValue) {
      mode = "inbox"
      url = this.inboxUrlValue
    } else if (this.element.querySelector('[aria-expanded="true"]')) {
      mode = "preview"
      url = this.recentUrlValue
    }
    const htmlResponse = mode !== "status"
    const version = revision
    const request = new AbortController()
    this.request = request
    this.showError(false)
    if (mode === "preview" && this.hasLoadingTarget) this.loadingTarget.classList.remove("d-none")
    try {
      const response = await fetch(url, {
        headers: { Accept: htmlResponse ? "text/html" : "application/json" },
        cache: "no-store", signal: request.signal
      })
      if (!response.ok) throw new Error("Notification refresh failed")
      const data = htmlResponse ? await response.text() : await response.json()
      if (version !== revision || !this.element.isConnected) {
        this.reloadInbox ||= reloadInbox
        return
      }
      if (mode === "inbox") {
        this.replaceContent(data, reloadInbox ? null : this.contentTarget.querySelector("[data-notification-items]"),
          reloadInbox ? null : this.contentTarget.querySelector("[data-notification-pages]"))
      } else if (mode === "preview") this.replaceContent(data)
      else this.updateBadge(data.unread_count)
    } catch (error) {
      this.reloadInbox ||= reloadInbox
      if (error.name !== "AbortError" && version === revision) this.showError(true)
    } finally {
      if (this.hasLoadingTarget) this.loadingTarget.classList.add("d-none")
      this.request = null
      if (this.refreshPending && this.element.isConnected) {
        this.refreshPending = false
        this.refresh()
      }
    }
  }

  async read(event) {
    event.preventDefault()
    if (readInFlight) return
    const form = event.target
    const data = new FormData(form)
    const modal = form.dataset.notificationModal === "true"
    this.saving = true
    readInFlight = true
    revision += 1
    this.request?.abort()
    this.showError(false)
    const buttons = Array.from(this.element.querySelectorAll('input[type="submit"], button[type="submit"]'))
    buttons.forEach(button => { button.disabled = true })
    try {
      const response = await fetch(form.action, {
        method: "POST", body: data, headers: {
          Accept: modal ? "text/vnd.turbo-stream.html" : "text/html", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        }, cache: "no-store"
      })
      if (!response.ok) throw new Error("Notification read failed")
      const html = await response.text()
      if (!this.element.isConnected) return
      revision += 1
      if (modal) {
        Turbo.renderStreamMessage(html)
        Modal.getInstance(this.element.closest(".modal"))?.hide()
        return
      }
      const retainedItems = this.contentTarget.querySelector("[data-notification-items]")
      const retainedPages = this.contentTarget.querySelector("[data-notification-pages]")
      if (retainedItems) {
        const row = form.closest("[data-notification-key]")
        const rows = row ? [row] : Array.from(retainedItems.querySelectorAll("[data-notification-key]"))
        this.markRowsRead(rows)
      }
      this.replaceContent(html, retainedItems, retainedPages)
      const focus = this.contentTarget.querySelector('input[type="submit"], a, button') ||
        this.element.querySelector('[data-bs-toggle="dropdown"]') || this.contentTarget
      focus.focus()
      this.dispatch("changed", { target: document, detail: { origin: this } })
    } catch (_error) {
      this.showError(true)
    } finally {
      buttons.forEach(button => { button.disabled = false })
      this.saving = false
      readInFlight = false
      this.dispatch("idle", { target: document })
      if (this.refreshPending) {
        this.refreshPending = false
        this.refresh()
      }
    }
  }

  markRowsRead(rows) {
    rows.forEach(item => {
      const readForm = item.querySelector("form")
      if (!readForm) return
      const badge = document.createElement("span")
      badge.className = "badge text-bg-secondary"
      badge.textContent = "Read"
      readForm.replaceWith(badge)
      item.classList.add("text-body-secondary", "bg-light")
    })
  }

  replaceContent(html, retainedItems = null, retainedPages = null) {
    const active = document.activeElement
    const restoreFocus = this.contentTarget.contains(active)
    const focusedRow = active?.closest("[data-notification-key]")
    const template = document.createElement("template")
    template.innerHTML = html // Escaped, server-rendered ERB fragment.
    const state = template.content.querySelector("[data-unread-count]")
    if (!state) throw new Error("Invalid notification response")
    const count = Number(state.dataset.unreadCount)
    if (!Number.isInteger(count) || count < 0) throw new Error("Invalid unread count")
    if (retainedItems) template.content.querySelector("[data-notification-items]").replaceWith(retainedItems)
    if (retainedPages) template.content.querySelector("[data-notification-pages]").replaceWith(retainedPages)
    this.contentTarget.replaceChildren(template.content)
    this.updateBadge(count)
    if (restoreFocus) {
      const row = Array.from(this.contentTarget.querySelectorAll("[data-notification-key]")).find(element =>
        element.dataset.notificationKey === focusedRow?.dataset.notificationKey &&
        element.dataset.notificationSource === focusedRow?.dataset.notificationSource)
      const selector = active.matches('input[type="submit"]') ? 'input[type="submit"]' : active.tagName.toLowerCase()
      const focus = row?.querySelector(selector) ||
        this.element.querySelector('[data-bs-toggle="dropdown"]') || this.contentTarget
      focus.focus()
    }
  }

  updateBadge(count) {
    if (this.hasBadgeTarget) this.badgeTarget.classList.toggle("d-none", count === 0)
    if (this.hasUnreadStatusTarget) this.unreadStatusTarget.textContent = `${count} unread notifications`
  }

  showError(show) {
    if (this.hasErrorTarget) this.errorTarget.classList.toggle("d-none", !show)
  }
}
